import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import MobileShell from "@/components/MobileShell";
import {
  useQueueAnnouncements, useSoundArm, ACCEPT_RECEPTION, playAnnouncement,
} from "@/lib/announcer";
import {
  ClipboardList, Clock, Activity, Receipt, RefreshCw, BellRing, UserCheck,
  CheckCircle2, Stethoscope, ChevronRight, Volume2, X,
} from "lucide-react";

const STATUS_META = {
  waiting: { label: "Waiting", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  in_progress: { label: "In Progress", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  billing: { label: "Cashier", cls: "bg-purple-500/20 text-purple-300 border-purple-500/40" },
  done: { label: "Done", cls: "bg-slate-600/30 text-slate-300 border-slate-600/50" },
  cancelled: { label: "Cancelled", cls: "bg-rose-500/20 text-rose-300 border-rose-500/40" },
};

const FILTER_LABEL = {
  all: "All",
  waiting: "Waiting",
  in_progress: "In Progress",
  billing: "Cashier",
  done: "Done",
};

function patientName(row) {
  return row?.patient_snapshot?.full_name || row?.patient_name || "—";
}
function patientMeta(row) {
  const s = row?.patient_snapshot || {};
  return [s.age ? `${s.age} y/o` : "", s.gender || ""].filter(Boolean).join(" · ");
}
function displayTime(row) {
  if (row?.reservation_time) return row.reservation_time;
  try {
    return new Date(row?.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}
function isReserved(row) {
  return row?.source === "reservation" || row?.priority === "reserved";
}

export default function ReceptionMobile() {
  const nav = useNavigate();
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  // Front desk hears its own calls plus the cashier's and the doctor's.
  const { armed: soundOn, arm: armSound } = useSoundArm();
  const callBusy = useRef(false);
  const { markSelfPlayed } = useQueueAnnouncements({ accept: ACCEPT_RECEPTION, enabled: soundOn });

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/queue/today");
      setQueue(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const active = useMemo(() => queue.filter((q) => q.status !== "cancelled"), [queue]);

  const counts = {
    all: active.length,
    waiting: active.filter((q) => q.status === "waiting").length,
    in_progress: active.filter((q) => q.status === "in_progress").length,
    billing: active.filter((q) => q.status === "billing").length,
    done: active.filter((q) => q.status === "done").length,
  };

  const visible = useMemo(() => {
    if (filter === "all") return active;
    return active.filter((q) => q.status === filter);
  }, [active, filter]);

  const drillTo = (key) => {
    setFilter(key);
    setTimeout(() => document.getElementById("queue-section")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  // Calling the cashier lane says "proceed to the cashier"; everyone else "to the reception".
  const callPatient = async (row) => {
    // TTS generation takes a beat — block the double-tap that lands in it.
    if (callBusy.current) return;
    callBusy.current = true;
    try {
      const callType = row.status === "billing" ? "cashier" : "reception";
      const { data } = await api.post(`/queue/${row.id}/announce`, { call_type: callType });
      markSelfPlayed(data.id);
      playAnnouncement(data);
      toast.success(`Calling ${patientName(row)}`);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      callBusy.current = false;
    }
  };

  const setStatus = async (row, status) => {
    try {
      await api.put(`/queue/${row.id}/status`, { status });
      setQueue((prev) => prev.map((x) => (x.id === row.id ? { ...x, status } : x)));
      toast.success(
        status === "billing" ? "Sent to cashier"
          : status === "done" ? "Marked done"
          : status === "cancelled" ? "Removed from queue"
          : "Updated"
      );
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  const arrive = async (row) => {
    const rid = row?.reservation_id || row?.id;
    try {
      await api.post(`/reservations/${rid}/arrive`);
      toast.success("Patient checked in");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  const btn = "inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-3 text-sm font-semibold active:scale-[0.98] transition";

  return (
    <MobileShell dark>
      <div className="px-5 pt-6 bg-slate-950 min-h-screen">
        {/* Heading */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-4xl font-extrabold leading-tight" style={{ fontFamily: "Work Sans" }}>eMedbay</div>
            <div className="text-sm font-bold text-amber-400 tracking-[0.32em] uppercase mt-1">Reception</div>
          </div>
          <button
            onClick={load}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl bg-white/10 border border-white/15 px-4 py-2 text-sm font-medium disabled:opacity-60"
            data-testid="reception-refresh"
          >
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Sound arming — required once before call-outs can play here */}
        {!soundOn ? (
          <button
            onClick={armSound}
            className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 py-3 text-sm font-bold text-white"
            data-testid="reception-enable-sound"
          >
            <Volume2 size={16} /> Tap to enable call-out sound
          </button>
        ) : (
          <div className="mt-4 flex items-center gap-2 text-xs text-emerald-400">
            <Volume2 size={14} /> Sound on — you&apos;ll hear cashier and doctor calls too
          </div>
        )}

        {/* Drillable tiles */}
        <div className="grid grid-cols-2 gap-3 mt-5">
          <button
            onClick={() => drillTo("all")}
            className={`text-left rounded-2xl p-4 bg-gradient-to-br from-blue-600 to-cyan-400 shadow-md active:scale-[0.99] ${filter === "all" ? "ring-2 ring-white/80" : ""}`}
            data-testid="tile-all"
          >
            <div className="flex items-center justify-between"><ClipboardList size={20} /><ChevronRight size={18} className="text-white/70" /></div>
            <div className="text-5xl font-extrabold mt-3 leading-none">{counts.all}</div>
            <div className="text-sm text-white/85 mt-2">Today&apos;s Queue</div>
          </button>
          <button
            onClick={() => drillTo("waiting")}
            className={`text-left rounded-2xl p-4 bg-slate-900 border active:scale-[0.99] ${filter === "waiting" ? "border-amber-400 ring-2 ring-amber-400/40" : "border-slate-800"}`}
            data-testid="tile-waiting"
          >
            <div className="flex items-center justify-between"><Clock size={20} className="text-amber-400" /><ChevronRight size={18} className="text-slate-500" /></div>
            <div className="text-5xl font-extrabold mt-3 leading-none">{counts.waiting}</div>
            <div className="text-sm text-slate-300 mt-2">Waiting</div>
          </button>
          <button
            onClick={() => drillTo("in_progress")}
            className={`text-left rounded-2xl p-4 bg-slate-900 border active:scale-[0.99] ${filter === "in_progress" ? "border-emerald-400 ring-2 ring-emerald-400/40" : "border-slate-800"}`}
            data-testid="tile-in-progress"
          >
            <div className="flex items-center justify-between"><Activity size={20} className="text-emerald-400" /><ChevronRight size={18} className="text-slate-500" /></div>
            <div className="text-5xl font-extrabold mt-3 leading-none">{counts.in_progress}</div>
            <div className="text-sm text-slate-300 mt-2">In Progress</div>
          </button>
          <button
            onClick={() => drillTo("billing")}
            className={`text-left rounded-2xl p-4 bg-slate-900 border active:scale-[0.99] ${filter === "billing" ? "border-purple-400 ring-2 ring-purple-400/40" : "border-slate-800"}`}
            data-testid="tile-billing"
          >
            <div className="flex items-center justify-between"><Receipt size={20} className="text-purple-400" /><ChevronRight size={18} className="text-slate-500" /></div>
            <div className="text-5xl font-extrabold mt-3 leading-none">{counts.billing}</div>
            <div className="text-sm text-slate-300 mt-2">At Cashier</div>
          </button>
        </div>

        {/* Queue list */}
        <section className="mt-7 pb-14" id="queue-section">
          <div className="flex items-end justify-between mb-3">
            <h2 className="text-xl font-extrabold" style={{ fontFamily: "Work Sans" }}>{FILTER_LABEL[filter]}</h2>
            <div className="text-xs text-slate-400">{visible.length} patients</div>
          </div>

          <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
            {["all", "waiting", "in_progress", "billing", "done"].map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold border ${filter === k ? "bg-white text-slate-900 border-white" : "bg-slate-900 text-slate-300 border-slate-700"}`}
                data-testid={`filter-${k}`}
              >
                {FILTER_LABEL[k]}
              </button>
            ))}
          </div>

          <div className="space-y-3" data-testid="queue-list">
            {visible.map((row) => {
              const status = row.status || "waiting";
              const sm = STATUS_META[status] || STATUS_META.waiting;
              const reserved = isReserved(row);
              return (
                <div key={row.id} className={`rounded-2xl p-4 border ${reserved ? "bg-red-500/10 border-red-400/30" : "bg-slate-900 border-slate-800"}`} data-testid={`queue-row-${row.id}`}>
                  <div onClick={() => row.patient_id && nav(`/patients/${row.patient_id}`)} className="cursor-pointer active:opacity-80">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] font-semibold rounded-full px-2.5 py-0.5 border ${sm.cls}`}>{sm.label}</span>
                      {reserved ? <span className="text-[11px] font-semibold rounded-full px-2.5 py-0.5 bg-red-600 text-white">Reserved</span> : null}
                      {row.queue_number ? <span className="text-[11px] font-mono text-cyan-300">#{row.queue_number}</span> : null}
                      <span className="text-[11px] text-slate-500 font-mono ml-auto">{displayTime(row)}</span>
                    </div>
                    <div className="text-xl font-extrabold mt-2 leading-tight truncate" style={{ fontFamily: "Work Sans" }}>{patientName(row)}</div>
                    <div className="text-xs text-slate-400 mt-1 truncate">
                      {patientMeta(row) || row.reason || "No details"}
                      {row.assigned_doctor_name ? ` · ${row.assigned_doctor_name}` : ""}
                    </div>
                  </div>

                  {reserved && status === "waiting" ? (
                    <button onClick={() => arrive(row)} className={`${btn} w-full mt-3 bg-emerald-600 text-white`} data-testid={`arrive-${row.id}`}>
                      <UserCheck size={16} /> Arrived / Check In
                    </button>
                  ) : null}

                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <button onClick={() => callPatient(row)} className={`${btn} bg-blue-600 text-white`} data-testid={`call-${row.id}`}>
                      <BellRing size={16} /> Call
                    </button>
                    {status === "billing" ? (
                      <button onClick={() => setStatus(row, "done")} className={`${btn} bg-slate-700 text-white`} data-testid={`done-${row.id}`}>
                        <CheckCircle2 size={16} /> Done
                      </button>
                    ) : status === "done" ? (
                      <button onClick={() => row.patient_id && nav(`/patients/${row.patient_id}`)} className={`${btn} bg-slate-800 border border-slate-700 text-slate-200`} data-testid={`chart-${row.id}`}>
                        <Stethoscope size={16} /> Chart
                      </button>
                    ) : (
                      <button onClick={() => setStatus(row, "billing")} className={`${btn} bg-purple-600 text-white`} data-testid={`cashier-${row.id}`}>
                        <Receipt size={16} /> To Cashier
                      </button>
                    )}
                  </div>

                  {status !== "done" ? (
                    <button onClick={() => setStatus(row, "cancelled")} className="w-full mt-2 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold text-rose-300 border border-rose-500/30 active:scale-[0.98]" data-testid={`remove-${row.id}`}>
                      <X size={14} /> Remove from queue
                    </button>
                  ) : null}
                </div>
              );
            })}

            {visible.length === 0 ? (
              <div className="rounded-2xl border border-slate-800 bg-slate-900 p-8 text-center text-slate-400">
                No patients in “{FILTER_LABEL[filter]}”.
                {filter !== "all" && active.length > 0 ? (
                  <button onClick={() => setFilter("all")} className="block mx-auto mt-3 text-sm font-semibold text-cyan-400" data-testid="show-all">
                    Show all {active.length} patients
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </MobileShell>
  );
}
